<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <ais-instant-search
    :index-name="algolia.indexProducts"
    :routing="algoliaRouting"
    :search-client="algolia.searchClient"
    :insights="true"
    :class-names="{
      'ais-InstantSearch': `header-search__wrapper ${view}`,
    }"
  >
    <ais-configure :rule-contexts.camel="['search-modal']" />

    <search-form
      ref="searchForm"
      :view="view"
      @update:query="input = $event"
      @focussed="searchFocussed = true"
    />

    <overlay
      key="search-modal"
      block-class="search-modal-overlay"
      direction="down"
      :namespace="namespace"
      :show-close="false"
      type="shelf"
    >
      <template #body="overlay">
        <search-modal
          :key="input"
          :overlay="overlay"
          :focus-elements="searchFormRefs"
          :query="input"
          :view="view"
          @update:query="input = $event"
        />
      </template>
    </overlay>
  </ais-instant-search>
</template>

<script>

/**
 * Vue: Header search (header-search)
 * -----------------------------------------------------------------------------
 * Header predictive search, Algolia powered.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import aa from "search-insights"
import algoliasearch from 'algoliasearch/lite'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings'
import { AisConfigure, AisInstantSearch } from 'vue-instantsearch/vue3/es'

import Overlay from '~global/overlay/overlay'
import SearchForm from '~async/search-form/search-form'
import SearchModal from '~async/search-modal/search-modal'

import { debounce } from '~/helpers/general'

const algoliaClient = algoliasearch(
  cnvs.algolia.credentials.appId,
  cnvs.algolia.credentials.apiKey,
)

export default {
  name: 'HeaderSearch',

  components: {
    AisInstantSearch,
    AisConfigure,
    Overlay,
    SearchModal,
    SearchForm,
  },

  props: {
    view: {
      // 'desktop-form', 'mobile-form' or 'toggle-modal'
      type: String,
      default: 'desktop-form',
    },
  },

  data() {
    return {
      input: '',
      // If user enters something in input.
      touched: false,
      searchRef: null,
      searchModalActive: false,
      searchFocussed: false,
      isActive: false,
      searchFormRefs: [],
      algolia: {
        indexProducts: this.$variable('algolia.indexes.products'),
        defaultFilters: this.$variable('algolia.defaultFilters'),
        searchClient: {
          ...algoliaClient,
          // Using arrow function to access component scope.
          search: (requests) => {
            // If search is not focused to not send any algolia queries.
            if (!this.searchFocussed) {
              return Promise.resolve({
                results: requests.map(() => ({
                  hits: [],
                  nbHits: 0,
                  nbPages: 0,
                  page: 0,
                  processingTimeMS: 0,
                  hitsPerPage: 0,
                  exhaustiveNbHits: false,
                  query: '',
                  params: '',
                })),
              })
            }

            return algoliaClient.search(requests)
          },
        },
        routing: {
          router: historyRouter(),
          stateMapping: singleIndexMapping(this.$variable('algolia.indexes.products')),
        },
      },
      pageType: this.$variable('page.type'),
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    algoliaRouting() {
      if (this.pageType !== 'search') {
        return null
      }

      return this.algolia.routing
    },

    namespace() {
      return this.view === 'desktop-form' ? 'search-modal' : 'search-modal search-modal-mobile'
    },
  },

  watch: {

    /**
     * Watch active overlay to reset current link.
     */
    async getActiveOverlay() {
      await this.$nextTick()

      if (this.getActiveOverlay?.namespace === this.namespace) {
        this.searchModalActive = true
        return
      }

      this.searchModalActive = false
    },

    isMobile() {
      // Close the search modal when mobile state changes
      if (this.searchModalActive) {
        this.closeOverlay(this.namespace)
        this.searchModalActive = false
        this.input = ''
      }
    },
  },

  mounted() {
    setTimeout(() => {
      const refs = []

      if (this.view === 'mobile-form' || !this.$refs.searchForm) {
        this.searchFormRefs = []
        return
      }

      if (this.$refs.searchForm.$refs.searchRef) {
        refs.push(this.$refs.searchForm.$refs.searchRef)
      }
      if (this.$refs.searchForm.$refs.searchClear) {
        refs.push(this.$refs.searchForm.$refs.searchClear)
      }

      this.searchFormRefs = refs
    }, 100) // Small delay to ensure everything is rendered

    const customerId = this.$variable('customer.id')

    aa('init', {
      appId: this.$variable('algolia.credentials.appId'),
      apiKey: this.$variable('algolia.credentials.apiKey'),
      useCookie: true,
    })

    if (this.$variable('customer.loggedIn') && customerId) {
      aa('setUserToken', String(customerId))
    }


    if (this.$variable('customer.loggedIn') && customerId) {
      // When user logged in, set the authenticated user token to the customer ID.
      aa('setAuthenticatedUserToken', String(customerId))
    } else {
      // When user log out, you can unset the authenticated token by passing undefined.
      aa('setAuthenticatedUserToken', undefined)
    }
  },

  created() {
    this.debouncedRefineResults = debounce(this.refineResults, this.settings.throttle)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Allows the search query to be updated from the parent.
     * @param {Event} event - Input event.
     * @param {Function} refine - Algolia refine function.
     */
    updateSearchQuery(event, refine) {
      const value = event?.currentTarget?.value || ''
      this.input = value
      this.touched = true

      this.debouncedRefineResults(refine, value)
    },

    /**
     * Allows the search query to be updated from the parent.
     * @param {Function} refine - Algolia refine function.
     * @param {String} query - Input field value.
     */
    refineResults(refine, query) {
      refine(query)
    },

    getValue(currentRefinement) {

      /**
       * Before interaction with search box, set input from currentRefinement
       * and fire event search-active
       */
      if (!this.touched) {
        this.input = currentRefinement
      }

      return this.input
    },

    /**
     * Clear search input and close search modal.
     * @param {Function} refine - Algolia refine function.
     */
    closeSearchModal(refine) {
      this.input = ''
      this.isActive = false
      this.closeOverlay(this.namespace)
      setTimeout(() => refine(''), this.settings.throttle)
    },
  },
}
</script>

<style lang="scss">
@import './header-search';
</style>
